import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { CarouselProvider, Slider, Slide, DotGroup, ButtonNext } from 'pure-react-carousel';

import VideoSlide from '../VideoSlide/VideoSlide';
import { getImageFilepath, formatParagraphs, getNavbarHeight, getPBPVideoSlides } from '../../../models/Utils';
import { ReactComponent as PlayButton } from '../../../assets/images/buttons/btn_play.svg';
import { ADAARIARoles, ADALandmarks, ADAWidgitRoles } from '../../../models/ADA';
import './FullScreenCarousel.scss';

const FullScreenCarousel = ({ data, active }) => {
  const controlRef = useRef(null);
  const carouselRef = useRef(null);
  const slides = getPBPVideoSlides(data);
  const [animating, setIsAnimating] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const isActive = false;
  const carouselContext = controlRef.current && controlRef.current.context;
  const configData = useSelector(state => state.config);
  const menuOpened = useSelector(state => state.data.menuOpened);

  useEffect(() => {
    if (controlRef.current) {
      setTimeout(() => {
        if (controlRef.current) {
          controlRef.current.context.setStoreState({ currentSlide: 0 });
        }
      }, 1000);
    }
  }, [active]);

  // effect to handle horizental swipe on desktop
  useEffect(() => {
    if (!carouselRef.current) {
      return;
    }
    let checkForScroll = true;
    let maxDeltaX = 0;
    const handleScroll = e => {
      const scrollThreshold = 2;
      if (checkForScroll && Math.abs(e.deltaY) < Math.abs(e.deltaX)) {
        if (Math.abs(e.deltaX) > Math.abs(maxDeltaX) && Math.abs(maxDeltaX) <= scrollThreshold) {
          maxDeltaX = e.deltaX;
        } else {
          checkForScroll = false;
          if (Math.abs(maxDeltaX) > scrollThreshold) {
            const carouselContext = controlRef.current.context;
            if (maxDeltaX > 0) {
              if (carouselContext.state.currentSlide < carouselContext.state.totalSlides - 1) {
                controlRef.current.context.setStoreState({
                  currentSlide: carouselContext.state.currentSlide + 1,
                });
              }
            } else {
              if (carouselContext.state.currentSlide > 0) {
                controlRef.current.context.setStoreState({
                  currentSlide: carouselContext.state.currentSlide - 1,
                });
              }
            }
          }
          maxDeltaX = 0;
          setTimeout(() => {
            checkForScroll = true;
          }, 1000);
        }
      }
    };

    // modern Chrome requires { passive: false } when adding event
    let supportsPassive = false;
    try {
      carouselRef.current.addEventListener(
        'test',
        null,
        Object.defineProperty({}, 'passive', {
          get: function () {
            supportsPassive = true;
          },
        })
      );
    } catch (e) {}

    const wheelOpt = supportsPassive ? { passive: false } : false;
    const wheelEvent = 'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel';
    carouselRef.current.addEventListener(wheelEvent, handleScroll, wheelOpt); // modern desktop
    return () => {
      carouselRef.current.removeEventListener(wheelEvent, handleScroll);
    };
  }, []);
  useEffect(() => {
    if (!carouselContext) {
      return;
    }
    function onChange() {
      setIsAnimating(true);
      setTimeout(() => {
        setIsAnimating(false);
      }, 500);
    }
    carouselContext.subscribe(onChange);
    return () => carouselContext.unsubscribe(onChange);
  }, [carouselContext]);

  const handleOnPlaying = (val) => {
   setVideoPlaying(val);
  };

  return (
    <div className="full-page-carousel" ref={carouselRef}>
      <CarouselProvider
        totalSlides={slides.length}
        naturalSlideWidth="100vw"
        naturalSlideHeight="100vh"
        dragStep={1}
        lockOnWindowScroll={true}
        isPlaying={isActive}
        interval={10000}
        dragEnabled={slides.length > 1}
        touchEnabled={slides.length > 1}
      >
        <Slider classNameAnimation="carousel" moveThreshold={0.02}>
          {slides.map((slide, i) => (
            <Slide key={slide.Key} index={i}>
              {slide.Video ? (
                <VideoSlide
                  data={{
                    Thumbnail: {
                      Image: slide.Thumbnail.Image,
                      Overlay: slide.Thumbnail.Overlay,
                      Opacity: slide.Thumbnail.Opacity,
                    },
                    Video: {
                      URL: slide.Video.URL,
                      Overlay: slide.Video.Overlay,
                      Opacity: slide.Video.Opacity,
                    },
                    Text: {
                      Header: slide.Header,
                    }
                  }}
                  active={(carouselContext && carouselContext.state && carouselContext.state.currentSlide === i) && active}
                  closeButton={true}
                  doNotHideLoading={true}
                  forceShowControls={false}
                  showPlayButton={false}
                  canPlay={true}
                  onPlaying={handleOnPlaying}
                />
              ) : null}
              <div
                className={`content-container ${!slide.Video ? 'text-slide' : ''}`}
                style={
                  slide.Background
                    ? {
                        backgroundImage: `url(${
                          configData.basePath +
                          configData.apiMediaPath +
                          getImageFilepath(slide.Background.Image)
                        })`,
                      }
                    : {}
                }
              >
                {slide.Background && slide.Background.Overlay ? (
                  <div
                    className="overlay"
                    style={{
                      backgroundColor: slide.Background.Overlay,
                      opacity: slide.Background.Opacity,
                    }}
                  ></div>
                ) : null}
                {slide.Video && slide.Video.Overlay ? (
                  <div
                    className="overlay"
                    style={{
                      backgroundColor: slide.Video.Overlay,
                      opacity: slide.Video.Opacity,
                    }}
                  ></div>
                ) : null}
                <div className={`content ${slide.Video && 'video-slide'}`} style={{top: slide.Video ? getNavbarHeight() + "px" : "", height: slide.Video ? "calc(100% - "+getNavbarHeight()*2+"px)" : ""}}>
                  <div className="width-wrapper">
                    <div className="content-wrapper">
                      {slide.Header ? <h3 /*tabIndex={(active && carouselContext && carouselContext.state.currentSlide === i && !menuOpened) ? 0 : -1}*/>{slide.Header}</h3> : null}
                      <div className="body">{formatParagraphs(slide.Body)}</div>
                      {slide.Video && 
                        <div className="video-play-button">
                          <PlayButton role={ADAWidgitRoles.Button} fill={"#000000"} aria-hidden="true" onClick={null} />
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </Slide>
          ))}
        </Slider>
        {active && carouselContext && carouselContext.state && carouselContext.state.currentSlide !== slides.length - 1 && !videoPlaying && <ButtonNext />}
        {active && <DotGroup ref={controlRef} style={{ visibility: slides.length > 1 ? '' : 'hidden' }} />}
      </CarouselProvider>
    </div>
  );
};

export default FullScreenCarousel;
